import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import useGetTemplateMetadata from '~/src/hooks/useGetTemplateMetadata';
import { getFilterCategoriesForRegion } from '~/src/utils/additionalRegionForms';
import { getLevelConfig, getBreadcrumbData, CourtFormLevel } from './helpers';
import { getRegion, Region } from '~/src/regions/config/utils';
import Breadcrumb from '~/src/components/Breadcrumb';
import { COURT_FORMS_ROUTE } from '~/src/pages/Library/LibraryPage';
import RegionalCourtFormContent from './RegionalCourtFormContent';

interface Props {
  routeUrl?: string;
  regionFilter?: Region;
  courtFormsRootUrl?: string;
}

const RegionalCourtFormContainer: React.FC<Props> = ({
  routeUrl,
  regionFilter,
  courtFormsRootUrl,
}) => {
  const effectiveRegion = regionFilter ?? getRegion();
  const { data: templateMetadata, isLoading } = useGetTemplateMetadata();
  const location = useLocation();
  const rootRouteUrl = routeUrl ?? COURT_FORMS_ROUTE;

  const filteredMetadata = useMemo(() => {
    if (!templateMetadata || !effectiveRegion) return templateMetadata;

    const categories = getFilterCategoriesForRegion(
      templateMetadata,
      effectiveRegion,
    );
    const filtered = Object.fromEntries(
      categories.map((c) => [c, templateMetadata.filter_categories[c]]),
    );
    return { ...templateMetadata, filter_categories: filtered };
  }, [templateMetadata, effectiveRegion]);

  if (isLoading || !filteredMetadata) {
    return <div>Loading...</div>;
  }

  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const level = queryParams.level as CourtFormLevel;
  const filterCategory = queryParams.filterCategory as string;
  const subFilterValue = (queryParams.subFilter ||
    queryParams.practiceArea) as string;

  const { itemsToRender, childLabel, childLevel } = getLevelConfig(
    level,
    filterCategory,
    filteredMetadata,
    effectiveRegion,
  );

  const breadcrumbData = getBreadcrumbData({
    rootRouteUrl,
    courtFormsRootUrl,
    level,
    filterCategory,
    subFilterValue,
  });

  return (
    <div>
      <Breadcrumb data={breadcrumbData} />
      <RegionalCourtFormContent
        level={level}
        filterCategory={filterCategory}
        subFilterValue={subFilterValue}
        itemsToRender={itemsToRender}
        childLabel={childLabel}
        childLevel={childLevel}
        rootRouteUrl={rootRouteUrl}
        region={effectiveRegion}
      />
    </div>
  );
};

export default RegionalCourtFormContainer;
